import { memo } from "react";
import type { MouseEvent } from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import type { BackgroundJob, BackgroundJobKind } from "@/api/background-job/_models/BackgroundJob";
import { formatDateTime } from "@/app/date.utils";
import { LIST_GRID_EXPANDED_INSET } from "@/components/list-grid/ListGridTemplate";
import { OverflowTooltip } from "@/components/OverflowTooltip";
import { TimeAgo } from "@/components/TimeAgo";
import { JobStatusChip } from "@/features/job/JobStatusChip";
import { JobDetailStatus } from "@/features/job/job-detail/JobDetailStatus";
import { toJobInputFieldList } from "@/features/job/job-detail/jobDetail.utils";
import { toJobDuration, toJobFileNameList, toJobSummary } from "@/features/job/job.utils";
import { JobListDownloadButton } from "./JobListDownloadButton";
import { JobListTemplate } from "./JobListTemplate";

interface JobListRowProps {
  isOpen: boolean;
  job: BackgroundJob;
  jobLabel: string;
  kind: BackgroundJobKind | undefined;
  /** The current time in ms, for a running job's duration. */
  now: number;
  onToggle: (jobId: string) => void;
  onSelect: (jobId: string) => void;
}

const OPEN_MARKER_PX = 3;

const JobListRowContent = (props: JobListRowProps) => {
  const { isOpen, job, jobLabel, kind, now, onToggle, onSelect } = props;
  const summary = toJobSummary(job);
  const fileNames = toJobFileNameList(job);
  let trigger = "Someone";
  if (job.trigger === "SCHEDULE") trigger = "Schedule";
  let result = summary || "—";
  let resultColour = "text.primary";
  if (job.status === "FAILED") resultColour = "error.main";
  if (job.status === "QUEUED" || job.status === "RUNNING") {
    result = "Running…";
    resultColour = "text.secondary";
  }
  const inputFields = toJobInputFieldList(job, kind);
  const meta = [
    ...inputFields.map((field) => `${field.label} ${String(field.value)}`),
    `Started by ${trigger}`,
    formatDateTime(job.startedAt ?? job.createdAt),
  ];

  const handleToggle = () => {
    onToggle(job.id);
  };

  const handleSelect = (event: MouseEvent) => {
    event.stopPropagation();
    onSelect(job.id);
  };

  return (
    <Box
      sx={{
        bgcolor: isOpen ? "action.hover" : "transparent",
        borderBottom: 1,
        borderColor: "divider",
        boxShadow: (theme) => (isOpen ? `inset ${OPEN_MARKER_PX}px 0 0 ${(theme.vars ?? theme).palette.primary.main}` : "none"),
      }}
    >
      <JobListTemplate
        onClick={handleToggle}
        sx={{
          cursor: "pointer",
          minHeight: 44,
          py: 0.5,
          "&:hover": { bgcolor: isOpen ? "transparent" : "action.hover" },
        }}
      >
        <IconButton
          aria-label={isOpen ? "Collapse job" : "Expand job"}
          size="small"
          sx={{ gridArea: "expand" }}
        >
          <KeyboardArrowDownIcon
            fontSize="small"
            sx={{
              transform: isOpen ? "rotate(180deg)" : "none",
              transition: "transform 160ms",
            }}
          />
        </IconButton>
        <OverflowTooltip title={jobLabel}>
          <Typography
            noWrap
            sx={{
              fontWeight: 600,
              gridArea: "job",
            }}
            variant="body2"
          >
            {jobLabel}
          </Typography>
        </OverflowTooltip>
        <Typography
          color="text.secondary"
          noWrap
          sx={{
            fontFamily: "monospace",
            gridArea: "subject",
          }}
          variant="body2"
        >
          {job.subject ?? "—"}
        </Typography>
        <Typography
          color="text.secondary"
          noWrap
          sx={{ gridArea: "trigger" }}
          variant="body2"
        >
          {trigger}
        </Typography>
        <Box sx={{ gridArea: "status" }}>
          <JobStatusChip status={job.status} />
        </Box>
        <TimeAgo
          iso={job.startedAt ?? job.createdAt}
          sx={{ gridArea: "started" }}
        />
        <Typography
          noWrap
          sx={{
            fontVariantNumeric: "tabular-nums",
            gridArea: "duration",
            textAlign: "right",
          }}
          variant="body2"
        >
          {toJobDuration(job, now)}
        </Typography>
        <OverflowTooltip title={result}>
          <Typography
            noWrap
            sx={{
              color: resultColour,
              gridArea: "summary",
            }}
            variant="body2"
          >
            {result}
          </Typography>
        </OverflowTooltip>
        <Tooltip title="Open job">
          <IconButton
            aria-label={`Open ${jobLabel}`}
            onClick={handleSelect}
            size="small"
            sx={{ gridArea: "actions" }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </JobListTemplate>
      <Collapse
        in={isOpen}
        timeout={150}
        unmountOnExit
      >
        <Stack
          spacing={1.5}
          sx={{
            pb: 2.5,
            pl: LIST_GRID_EXPANDED_INSET,
            pr: 3,
            pt: 0.5,
          }}
        >
          <JobDetailStatus job={job} />
          <Stack
            direction="row"
            sx={{
              color: "text.secondary",
              columnGap: 2,
              flexWrap: "wrap",
              rowGap: 0.5,
              typography: "body2",
            }}
          >
            {meta.map((text) => (
              <Box
                component="span"
                key={text}
                sx={{ whiteSpace: "nowrap" }}
              >
                {text}
              </Box>
            ))}
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              flexWrap: "wrap",
            }}
            useFlexGap
          >
            {fileNames.map((fileName) => (
              <JobListDownloadButton
                fileName={fileName}
                jobId={job.id}
                key={fileName}
              />
            ))}
          </Stack>
        </Stack>
      </Collapse>
    </Box>
  );
};

/** One job: what ran, for what, who started it and how it went; opening it shows a short summary. Memoised. */
export const JobListRow = memo(JobListRowContent);
